import { LocationEntity } from '@peritasai/repository';
import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getCaseWindow } from '../../../shared';
import { useAssignToCaseService } from '../services/assign-to-case.service';
import type { AssignToCaseCase } from '../types/assign-to-case';
import { AssignToCaseStep } from '../types/assign-to-case';

type UseAssignToCaseDataResult = {
  step: AssignToCaseStep;
  setStep: Dispatch<SetStateAction<AssignToCaseStep>>;
  locations: LocationEntity[];
  cases: AssignToCaseCase[];
  visibleLocations: LocationEntity[];
  visibleCases: AssignToCaseCase[];
  showAllLocations: boolean;
  setShowAllLocations: Dispatch<SetStateAction<boolean>>;
  showAllCases: boolean;
  setShowAllCases: Dispatch<SetStateAction<boolean>>;
  loadLocations: () => Promise<void>;
  loadCases: (locationId: string) => Promise<void>;
  refreshCases: (locationId: string) => Promise<void>;
};

export const useAssignToCaseData = (): UseAssignToCaseDataResult => {
  const [step, setStep] = useState<AssignToCaseStep>(
    AssignToCaseStep.LOADING_LOCATIONS
  );
  const [locations, setLocations] = useState<LocationEntity[]>([]);
  const [cases, setCases] = useState<AssignToCaseCase[]>([]);
  const [showAllLocations, setShowAllLocations] = useState(false);
  const [showAllCases, setShowAllCases] = useState(false);

  const assignToCaseService = useAssignToCaseService();
  //breaks useEffect dependcy loop
  const assignToCaseServiceRef = useRef(assignToCaseService);
  assignToCaseServiceRef.current = assignToCaseService;

  const visibleLocations = useMemo(
    () => (showAllLocations ? locations : locations.slice(0, 5)),
    [locations, showAllLocations]
  );

  const sortedCases = useMemo(
    () =>
      [...cases].sort(
        (a, b) => a.scheduledDateTime.getTime() - b.scheduledDateTime.getTime()
      ),
    [cases]
  );

  const visibleCases = useMemo(
    () => (showAllCases ? sortedCases : getCaseWindow(sortedCases)),
    [showAllCases, sortedCases]
  );

  const fetchCases = useCallback(async (locationId: string) => {
    const data = await assignToCaseServiceRef.current.getCasesForLocation(locationId);
    setCases(data);
  }, []);

  const loadLocations = useCallback(async () => {
    setStep(AssignToCaseStep.LOADING_LOCATIONS);
    try {
      const data = await  assignToCaseServiceRef.current.getLocations();
      if (data.length === 0) {
        setStep(AssignToCaseStep.ERROR_LOCATIONS);
        return;
      }
      setLocations(data);
      setShowAllLocations(false);
      setStep(AssignToCaseStep.SELECT_LOCATION);
    } catch (error) {
      console.error('Failed to load sidehub locations', error);
      setStep(AssignToCaseStep.ERROR_LOCATIONS);
    }
  }, []);

  const loadCases = useCallback(
    async (locationId: string) => {
      setStep(AssignToCaseStep.LOADING_CASES);
      setShowAllCases(false);
      try {
        await fetchCases(locationId);
        setStep(AssignToCaseStep.SELECT_CASE);
      } catch (error) {
        console.error('Failed to load cases', error);
        setStep(AssignToCaseStep.ERROR_CASES);
      }
    },
    [fetchCases]
  );

  const refreshCases = useCallback(
    async (locationId: string) => {
      try {
        await fetchCases(locationId);
      } catch (error) {
        console.error('Failed to refresh cases', error);
      }
    },
    [fetchCases]
  );

  useEffect(() => {
    void loadLocations();
  }, [loadLocations]);

  return {
    step,
    setStep,
    locations,
    cases,
    visibleLocations,
    visibleCases,
    showAllLocations,
    setShowAllLocations,
    showAllCases,
    setShowAllCases,
    loadLocations,
    loadCases,
    refreshCases,
  };
};
